import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { IMAGES } from "@/lib/images";

// ---------- HERO ----------
function Hero() {
  const { t } = useI18n();
  const headlineLines = t("hero.headlineLines");
  const metrics = t("hero.metrics");

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgRotate = useTransform(scrollYProgress, [0, 1], [0, -2]);

  return (
    <section
      ref={heroRef}
      className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden"
      data-testid="hero-section"
    >
      {/* Ambient sage/gold background layers */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full bg-[#EAEFEA] opacity-70 blur-3xl" />
        <div className="absolute top-40 right-[-100px] w-[380px] h-[380px] rounded-full bg-[#F7F2E6] opacity-70 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #1C352D 1px, transparent 0)",
            backgroundSize: "28px 28px",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-end">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#1C352D]/12 bg-[#F8F7F4]/70 backdrop-blur px-3 py-1.5"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#C59B27]" />
            <span className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#1C352D]">
              {t("hero.eyebrow")}
            </span>
          </motion.div>

          <h1 className="mt-6 font-serif text-[38px] leading-[1.02] sm:text-[54px] md:text-[64px] lg:text-[72px] text-[#1C352D] tracking-tight">
            {headlineLines.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ delay: 0.15 + i * 0.12, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="block"
                >
                  {i === 1 ? (
                    <span className="italic text-[#C59B27]">{line}</span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="mt-7 max-w-[52ch] text-[15px] sm:text-[16px] leading-relaxed text-[#4A5D55]"
          >
            {t("hero.subtext")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.6 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <a
              href="#projects"
              className="group inline-flex items-center gap-2 rounded-full bg-[#1C352D] pl-5 pr-2 py-2 text-[13px] font-medium text-[#F8F7F4] hover:bg-[#2D4A3E] transition-colors"
              data-testid="hero-cta-primary"
            >
              <span>{t("hero.ctaPrimary")}</span>
              <span className="ml-1 inline-flex w-8 h-8 rounded-full bg-[#D4AF37] items-center justify-center text-[#1C352D] group-hover:rotate-45 transition-transform">
                <ArrowUpRight className="w-3.5 h-3.5" />
              </span>
            </a>
            <a
              href="#how-it-works"
              className="inline-flex items-center gap-2 rounded-full border border-[#1C352D]/20 px-4 py-2.5 text-[13px] text-[#1C352D] hover:border-[#C59B27] hover:text-[#C59B27] transition-colors"
              data-testid="hero-cta-secondary"
            >
              {t("hero.ctaSecondary")}
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </motion.div>
        </div>

        {/* Hero image — clipped frame with subtle parallax + gold spotlight */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 1 }}
            style={{ y: imgY, rotate: imgRotate }}
            className="relative aspect-[4/5] w-full max-w-[460px] ml-auto"
          >
            <div className="absolute -inset-6 rounded-[28px] bg-[#D4AF37]/12 blur-2xl" />
            <div className="relative h-full w-full overflow-hidden rounded-[6px] border border-[#1C352D]/10 shadow-[0_30px_80px_-30px_rgba(28,53,45,0.35)]">
              <img
                src={IMAGES.hero}
                alt="Bosco Verticale Milano"
                className="w-full h-full object-cover"
                loading="eager"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1C352D]/40 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-[10px] font-mono uppercase tracking-[0.24em] text-[#F8F7F4]">
                <span>{t("hero.imageCaption")}</span>
                <span className="text-[#D4AF37]">45°28'N</span>
              </div>
            </div>
            {/* corner ticks */}
            <span className="absolute -top-2 -left-2 w-4 h-4 border-t-2 border-l-2 border-[#D4AF37]" />
            <span className="absolute -bottom-2 -right-2 w-4 h-4 border-b-2 border-r-2 border-[#D4AF37]" />
          </motion.div>
        </div>
      </div>

      {/* Metrics strip */}
      <div className="relative mt-16 md:mt-20 mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-3 border-y border-[#1C352D]/12">
          {metrics.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
              className={`py-6 md:py-8 md:px-6 flex flex-col ${i > 0 ? "md:border-l border-[#1C352D]/12" : ""}`}
              data-testid={`hero-metric-${i}`}
            >
              <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#C59B27]">{m.label}</div>
              <div className="mt-2 flex items-baseline gap-2">
                <div className="font-serif text-3xl sm:text-4xl text-[#1C352D]">{m.value}</div>
                <div className="text-xs text-[#5C6B64]">{m.suffix}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- MARQUEE ----------
function Marquee() {
  const { t } = useI18n();
  const text = t("marquee.text");
  return (
    <div className="relative bg-[#1C352D] text-[#EAEFEA] border-y border-[#D4AF37]/25 py-4 overflow-hidden" data-testid="marquee">
      <motion.div
        className="flex whitespace-nowrap gap-16 will-change-transform"
        animate={{ x: ["0%", "-50%"] }}
        transition={{ duration: 50, ease: "linear", repeat: Infinity }}
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <span key={i} className="text-[11px] sm:text-[12px] font-mono uppercase tracking-[0.28em] flex items-center gap-4">
            <span className="text-[#D4AF37]">✦</span>
            <span>{text}</span>
          </span>
        ))}
      </motion.div>
    </div>
  );
}

// ---------- HOW IT WORKS ----------
function HowItWorks() {
  const { t } = useI18n();
  const steps = t("howItWorks.steps");
  return (
    <section id="how-it-works" className="relative py-24 md:py-36" data-testid="how-it-works-section">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="max-w-3xl">
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#C59B27]">{t("howItWorks.eyebrow")}</div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl md:text-5xl text-[#1C352D] leading-[1.05] tracking-tight">
            {t("howItWorks.title")}
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed text-[#4A5D55]">{t("howItWorks.subtitle")}</p>
        </div>

        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
          {steps.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
              className="relative"
              data-testid={`step-${i}`}
            >
              <div className="flex items-baseline gap-3">
                <span className="font-serif italic text-[#C59B27] text-2xl">{s.num}</span>
                <span className="h-px flex-1 bg-[#1C352D]/15" />
              </div>
              <h3 className="mt-4 font-serif text-[22px] text-[#1C352D] leading-tight">{s.title}</h3>
              <p className="mt-3 text-[13.5px] leading-relaxed text-[#5C6B64]">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- PROJECTS ----------
function Projects() {
  const { t } = useI18n();
  const cards = t("projects.cards");
  const labels = t("projects.labels");
  const images = [IMAGES.hero, IMAGES.portaNuova, IMAGES.residentialCurved];

  return (
    <section id="projects" className="relative py-20 md:py-32 bg-[#EFECE6]" data-testid="projects-section">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#C59B27]">{t("projects.eyebrow")}</div>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl md:text-5xl text-[#1C352D] leading-[1.05] tracking-tight">{t("projects.title")}</h2>
          </div>
          <p className="max-w-md text-[14px] leading-relaxed text-[#4A5D55]">{t("projects.subtitle")}</p>
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {cards.map((c, i) => (
            <motion.article
              key={c.id}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08, duration: 0.6 }}
              className="group relative flex flex-col bg-[#F8F7F4] border border-[#1C352D]/8 rounded-[2px] overflow-hidden hover:border-[#C59B27]/60 transition-colors"
              data-testid={`project-card-${c.id}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={images[i % images.length]} alt={c.title} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1C352D]/60 via-transparent to-transparent" />
                <div className="absolute top-3 left-3 rounded-full bg-[#F8F7F4]/90 backdrop-blur px-3 py-1 text-[10px] font-mono uppercase tracking-[0.2em] text-[#1C352D]" data-testid={`project-badge-${c.id}`}>{c.badge}</div>
                <div className="absolute bottom-3 left-3 right-3 text-[10px] font-mono uppercase tracking-[0.2em] text-[#F8F7F4]/90">{c.category}</div>
              </div>
              <div className="p-6">
                <h3 className="font-serif text-[22px] text-[#1C352D] leading-tight">{c.title}</h3>
                <div className="mt-1 text-[11.5px] font-mono uppercase tracking-[0.2em] text-[#5C6B64]">{c.location}</div>

                <div className="mt-5 grid grid-cols-3 gap-3 text-[11px]">
                  <div>
                    <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#C59B27]">{labels.yield}</div>
                    <div className="mt-1 font-serif text-[18px] text-[#1C352D]">{c.yield}</div>
                  </div>
                  <div>
                    <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#C59B27]">{labels.duration}</div>
                    <div className="mt-1 font-serif text-[18px] text-[#1C352D]">{c.duration}</div>
                  </div>
                  <div>
                    <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#C59B27]">{labels.min}</div>
                    <div className="mt-1 font-serif text-[18px] text-[#1C352D]">{c.min}</div>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex items-center justify-between text-[11px] mb-1.5">
                    <span className="text-[#5C6B64]">{labels.funded}</span>
                    <span className="font-mono text-[#1C352D]">{c.funded}% · {c.target}</span>
                  </div>
                  <div className="h-1 bg-[#1C352D]/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${c.funded}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 1.2, delay: 0.2 }}
                      className="h-full bg-[#C59B27]"
                    />
                  </div>
                </div>

                <a href="#contact" className="mt-6 inline-flex items-center gap-1.5 text-[12px] font-medium text-[#1C352D] hover:text-[#C59B27] group/link" data-testid={`project-cta-${c.id}`}>
                  {labels.invest}
                  <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- BENTO ----------
function Bento() {
  const { t } = useI18n();
  const items = t("bento.items");
  const styles = [
    { span: "md:col-span-8 md:row-span-2", bg: "bg-[#1C352D] text-[#F8F7F4]", dark: true },
    { span: "md:col-span-4", bg: "bg-[#F8F7F4] border border-[#1C352D]/12", dark: false },
    { span: "md:col-span-4", bg: "bg-[#EAEFEA] text-[#1C352D]", dark: false },
    { span: "md:col-span-8", bg: "bg-[#F8F7F4] border border-[#D4AF37]/40", dark: false },
  ];

  return (
    <section id="bento" className="relative py-24 md:py-36" data-testid="bento-section">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#C59B27]">{t("bento.eyebrow")}</div>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl sm:text-4xl md:text-5xl text-[#1C352D] leading-[1.05] tracking-tight">{t("bento.title")}</h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 md:auto-rows-[minmax(200px,auto)] gap-4 md:gap-5">
          {items.map((it, i) => {
            const s = styles[i];
            const isDark = s.dark;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.08, duration: 0.6 }}
                className={`${s.span} ${s.bg} rounded-[6px] p-7 md:p-9 flex flex-col justify-between relative overflow-hidden`}
                data-testid={`bento-item-${i}`}
              >
                {i === 0 && (
                  <div className="pointer-events-none absolute -right-16 -bottom-24 w-[380px] h-[380px] rounded-full border border-[#D4AF37]/30" />
                )}
                {i === 3 && (
                  <div className="pointer-events-none absolute inset-0 opacity-40">
                    <img src={IMAGES.woodSlats} alt="" className="absolute right-[-20%] bottom-[-30%] w-[70%] object-cover opacity-70 rounded-[6px]" />
                  </div>
                )}
                <div className="relative">
                  <div className={`text-[10px] font-mono uppercase tracking-[0.28em] ${isDark ? "text-[#D4AF37]" : "text-[#C59B27]"}`}>0{i + 1} / 04</div>
                  <h3 className={`mt-5 font-serif text-2xl md:text-3xl leading-tight ${isDark ? "text-[#F8F7F4]" : "text-[#1C352D]"}`}>{it.title}</h3>
                </div>
                <p className={`relative mt-6 text-[13.5px] leading-relaxed max-w-[46ch] ${isDark ? "text-[#F8F7F4]/75" : "text-[#4A5D55]"}`}>{it.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ---------- TRANSPARENCY ----------
function Transparency() {
  const { t } = useI18n();
  const body = t("transparency.body");
  const pillars = t("transparency.pillars");
  return (
    <section id="transparency" className="relative py-24 md:py-36 bg-[#1C352D] text-[#EAEFEA]" data-testid="transparency-section">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#D4AF37]">{t("transparency.eyebrow")}</div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl md:text-5xl text-[#F8F7F4] leading-[1.05] tracking-tight">{t("transparency.title")}</h2>
          <a href="#contact" className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-5 py-2.5 text-[12.5px] text-[#F8F7F4] hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#1C352D] transition-colors" data-testid="transparency-cta">
            {t("transparency.cta")}
            <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>
        <div className="lg:col-span-7">
          <div className="space-y-6 text-[15px] leading-relaxed text-[#EAEFEA]/80">
            {body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {pillars.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="border-t border-[#D4AF37]/30 pt-5"
              >
                <div className="font-serif italic text-[#D4AF37] text-xl">{p.k}</div>
                <div className="mt-2 font-serif text-lg text-[#F8F7F4]">{p.t}</div>
                <p className="mt-2 text-[12.5px] leading-relaxed text-[#EAEFEA]/70">{p.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- CONTACT FORM ----------
function Contact() {
  const { t, lang } = useI18n();
  const [values, setValues] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const onSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!values.name.trim()) errs.name = t("contact.required");
    if (!values.email.trim()) errs.email = t("contact.required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) errs.email = t("contact.invalidEmail");
    if (!values.message.trim()) errs.message = t("contact.required");
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSubmitting(true);
    // frontend-only: navigate to thank you
    setTimeout(() => {
      navigate("/thank_you");
    }, 300);
  };

  return (
    <section id="contact" className="relative py-24 md:py-36" data-testid="contact-section">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#C59B27]">{t("contact.eyebrow")}</div>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl md:text-5xl text-[#1C352D] leading-[1.05] tracking-tight">{t("contact.title")}</h2>
          <p className="mt-5 max-w-md text-[14.5px] leading-relaxed text-[#4A5D55]">{t("contact.subtitle")}</p>

          <div className="mt-10 rounded-[6px] border border-[#1C352D]/10 bg-[#F8F7F4] p-6">
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#C59B27]">Advisor</div>
            <div className="mt-2 font-serif text-lg text-[#1C352D]">advisor@ecominerali.com</div>
            <div className="mt-1 text-[12.5px] text-[#5C6B64]">+39 02 8974 6210 · Lun–Ven 9–18</div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={onSubmit} className="rounded-[6px] bg-[#1C352D] text-[#EAEFEA] p-8 md:p-10" data-testid="contact-form">
            <div className="space-y-6">
              <Field
                id="name"
                label={t("contact.fields.nameLabel")}
                placeholder={t("contact.fields.namePh")}
                value={values.name}
                onChange={(v) => setValues((s) => ({ ...s, name: v }))}
                error={errors.name}
                testid="form-name"
              />
              <Field
                id="email"
                type="email"
                label={t("contact.fields.emailLabel")}
                placeholder={t("contact.fields.emailPh")}
                value={values.email}
                onChange={(v) => setValues((s) => ({ ...s, email: v }))}
                error={errors.email}
                testid="form-email"
              />
              <Field
                id="message"
                as="textarea"
                label={t("contact.fields.messageLabel")}
                placeholder={t("contact.fields.messagePh")}
                value={values.message}
                onChange={(v) => setValues((s) => ({ ...s, message: v }))}
                error={errors.message}
                testid="form-message"
              />
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              <p className="text-[11.5px] leading-relaxed text-[#EAEFEA]/60 max-w-sm">{t("contact.consent")}</p>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#1C352D] pl-5 pr-2 py-2 text-[13px] font-medium hover:bg-[#F7F2E6] transition-colors disabled:opacity-70"
                data-testid="form-submit"
              >
                {t("contact.submit")}
                <span className="inline-flex w-8 h-8 rounded-full bg-[#1C352D] items-center justify-center text-[#D4AF37]">
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ id, label, placeholder, value, onChange, error, as, type, testid }) {
  const cls = `w-full bg-transparent border-b border-[#EAEFEA]/25 focus:border-[#D4AF37] outline-none py-3 text-[15px] placeholder:text-[#EAEFEA]/40 text-[#F8F7F4] focus:pl-1 transition-[border-color,padding]`;
  return (
    <label className="block" htmlFor={id}>
      <span className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#D4AF37]">{label}</span>
      {as === "textarea" ? (
        <textarea id={id} rows={4} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={cls + " resize-none pt-3"} data-testid={testid} />
      ) : (
        <input id={id} type={type || "text"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={cls} data-testid={testid} />
      )}
      {error && <span className="mt-1 block text-[11px] text-[#F7C971]">{error}</span>}
    </label>
  );
}

// ---------- PAGE ----------
export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <HowItWorks />
      <Projects />
      <Bento />
      <Transparency />
      <Contact />
    </>
  );
}
